import { useEffect, useRef, useState } from 'react';

import ivclass from '@invideoio/web-shared/Modules/IVClass';
import { PlatformType } from '@invideoio/web-shared/constants/common';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { SimpleHeadingType } from 'src/types';

/**
 * invideo AI pricing.
 *
 * Renders the same Glass section the marketing site shows on `/` and
 * `/pricing`, vendored verbatim into src/vendor/pricing-glass — same design,
 * same country-group catalogue selection, same influencer discounts — on a
 * dark full-width band, because these templates are white.
 *
 * Loaded lazily and client-only: the chunk (JS + the section's CSS and four
 * font faces) is only requested once the section nears the viewport, so the
 * page's SSR output and TTFB are unchanged and pages that never scroll this
 * far never download it. The v11 section it replaces was already
 * client-fetched, so nothing that used to be in the HTML has left it.
 *
 * Studio pricing is a different component entirely
 * (src/components/pricing-plan/index.tsx) and is deliberately untouched — the
 * page picks between them on `baseAppType`.
 */
const AiGlassPricing = dynamic(
  () =>
    import('src/vendor/pricing-glass').then(mod => ({
      default: mod.AiGlassPricing,
    })),
  { ssr: false },
);

/** How early the chunk is fetched, in px of scroll ahead of the section. */
const PREFETCH_MARGIN = '600px';

/**
 * Sections on these templates carry top padding only, so a band with none sits
 * flush against the fold above it. Same rhythm as the section that follows
 * (`xl:iv-pt-200 iv-pt-150 sm:iv-pt-100`), so the band sits in equal white
 * space. Margin, not padding: the space has to stay white. (It collapses with
 * the mount wrapper's own `sm:iv-mt-20`, it does not add to it.)
 */
const SPACE_ABOVE = 'xl:iv-mt-200 iv-mt-150 sm:iv-mt-100';

/**
 * Height held while the chunk and the plans request are in flight, so the folds
 * below do not jump when the ~1250px band arrives. Just under the rendered
 * height on desktop and phone, and dropped once the section has content — the
 * Business tab is shorter than this.
 */
const PLACEHOLDER = 'iv-min-h-[1200px]';

/** The band's ground (`--background-primary` upstream). */
const BAND_GROUND = '#141414';

interface PricingPlanProps {
  platform: PlatformType;
  theme?: 'light' | 'dark';
  pricingTitle?: SimpleHeadingType;
}

/*
 * `theme` and `pricingTitle` are still accepted so the five call sites
 * (MakePageContainerV3, AdPageContainer, CreatePage, HomePageAiV2, HomepageAI)
 * need no change, but neither applies any more: the band is always dark, and
 * the section renders its own localized heading — which is what parity with
 * the landing means. A CMS/prop heading would silently de-localize it.
 */
const PricingPlanAI = (_props: PricingPlanProps) => {
  const router = useRouter();
  const ref = useRef<HTMLElement | null>(null);
  const bandRef = useRef<HTMLDivElement | null>(null);
  const [nearViewport, setNearViewport] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (typeof IntersectionObserver === 'undefined') {
      setNearViewport(true);
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: PREFETCH_MARGIN },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Full-bleed out of the centred container. Measured rather than `100vw`,
  // which includes a classic scrollbar and would overhang by its width.
  useEffect(() => {
    const element = bandRef.current;
    if (!element) return;
    const apply = () =>
      element.style.setProperty(
        '--iv-ai-pricing-vw',
        `${document.documentElement.clientWidth}px`,
      );
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, []);

  // Release the placeholder once the section has rendered plans — or its
  // error state, which is short and must not sit in a 1200px block.
  useEffect(() => {
    const element = bandRef.current;
    if (!element || ready) return;
    const check = () => {
      if (element.querySelector('.pricing-glass, [role="alert"]')) {
        setReady(true);
        return true;
      }
      return false;
    };
    if (check()) return;
    const observer = new MutationObserver(() => {
      if (check()) observer.disconnect();
    });
    observer.observe(element, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [ready]);

  return (
    <section id="iv-ai-pricing" ref={ref} className={ivclass(SPACE_ABOVE)}>
      <div
        ref={bandRef}
        className={ivclass(!ready && PLACEHOLDER)}
        style={{
          backgroundColor: BAND_GROUND,
          width: 'var(--iv-ai-pricing-vw, 100vw)',
          maxWidth: '100vw',
          marginLeft: 'calc(50% - var(--iv-ai-pricing-vw, 100vw) / 2)',
          marginRight: 'calc(50% - var(--iv-ai-pricing-vw, 100vw) / 2)',
        }}
      >
        {nearViewport && <AiGlassPricing locale={router.locale} />}
      </div>
    </section>
  );
};

export default PricingPlanAI;
